import { Icon } from '@iconify/react';
import React, {
  forwardRef,
  useMemo,
  useState,
  useImperativeHandle,
  useEffect,
} from 'react';
import { Button } from '@nextui-org/react';
import { Settlement } from 'common/src/utils/types';
import { formatMoneyUSD } from '../../utils/functions';
import { useTranslation } from '../../../../common/src/hooks/useTranslation';
import { TH_BASE_CLASS, TableExportRef } from '../../utils/table-styles';
import { exportTableToExcel } from '../../utils/export-to-excel';
import { REVENUE_TYPE_LABELS } from '../../utils/revenue-types';

interface Props {
  settlements: Settlement[];
  activeTab: string;
}

interface FlowRow {
  label: string;
  isTotal?: boolean;
  revenue: number;
  commission: number;
  tax: number;
  otherCosts: number;
  repacking: number;
  freight: number;
  fob: number;
  revenuesByType: Record<string, number>;
}

type SortKey = 'label' | 'revenue' | 'commission' | 'tax' | 'otherCosts' | 'repacking' | 'freight' | 'fob' | 'fobAjustado' | string;

type ColumnColor = 'green' | 'red' | 'blue';

const COLOR_CLASSES: Record<ColumnColor, { bar: string; text: string; header: string }> = {
  green: {
    bar: 'bg-emerald-500/[0.14] dark:bg-emerald-400/[0.18]',
    text: 'text-emerald-700 dark:text-emerald-400',
    header: 'text-emerald-600 dark:text-emerald-400',
  },
  red: {
    bar: 'bg-red-500/[0.14] dark:bg-red-400/[0.18]',
    text: 'text-red-700 dark:text-red-400',
    header: 'text-red-600 dark:text-red-400',
  },
  blue: {
    bar: 'bg-blue-500/[0.14] dark:bg-blue-400/[0.18]',
    text: 'text-blue-700 dark:text-blue-400',
    header: 'text-blue-600 dark:text-blue-400',
  },
};

const COLUMN_COLORS: Record<string, ColumnColor> = {
  revenue: 'green',
  commission: 'red',
  tax: 'red',
  otherCosts: 'red',
  repacking: 'red',
  freight: 'red',
  fob: 'blue',
  fobAjustado: 'blue',
};

const getColumnColor = (columnKey: string): ColumnColor => {
  return COLUMN_COLORS[columnKey] || 'green'; // otros ingresos default to green
};

const FlowsTable = forwardRef<TableExportRef, Props>(({ settlements, activeTab }, ref) => {
  const { t } = useTranslation('settlements');
  const [sortKey, setSortKey] = useState<SortKey>('revenue');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  // Show top 10 data rows by default; totals row is always visible.
  const [visibleCount, setVisibleCount] = useState(10);
  useEffect(() => {
    setVisibleCount(10);
  }, [activeTab]);

  const { rows, revenueTypes } = useMemo(() => {
    if (!settlements || settlements.length === 0) return { rows: [], revenueTypes: [] };

    const revenueTypeSet = new Set<string>();

    const accumulate = (subset: Settlement[]) => {
      let revenue = 0;
      let commission = 0;
      let tax = 0;
      let otherCosts = 0;
      let repacking = 0;
      let freight = 0;
      const revenuesByType: Record<string, number> = {};

      subset.forEach((s) => {
        const ex = s.exchange_rate || 1;
        revenue += (s.total_revenue_local || 0) / ex;
        commission += (s.total_commission_local || 0) / ex;
        tax += (s.total_tax_local || 0) / ex;
        otherCosts += (s.total_other_costs_local || 0) / ex;
        repacking += (s.total_repacking_costs_local || 0) / ex;
        freight += s.total_freight_usd || 0;

        // Collect detail revenues
        s.settlement_details?.forEach((d) => {
          (d.settlement_detail_revenues || []).forEach((r: any) => {
            const type = r.revenue_type || 'other';
            const amountUsd = r.amount_usd || 0;
            revenueTypeSet.add(type);
            revenuesByType[type] = (revenuesByType[type] || 0) + amountUsd;
          });
        });
      });

      const fob = revenue - commission - tax - otherCosts - repacking - freight;

      return { revenue, commission, tax, otherCosts, repacking, freight, fob, revenuesByType };
    };

    // Group by active tab
    const groups: Record<string, Settlement[]> = {};
    settlements.forEach((s) => {
      let key: string;
      if (activeTab === 'client') {
        key = s.receiver || t('Sin Cliente');
      } else if (activeTab === 'variety') {
        // For variety, we expand settlement details
        return; // handled below
      } else {
        key = s.market?.country || t('Sin País');
      }
      if (!groups[key]) groups[key] = [];
      groups[key].push(s);
    });

    let dataRows: FlowRow[];

    if (activeTab === 'variety') {
      // Group by variety from settlement details
      const varietyData: Record<string, { revenue: number; commission: number; tax: number; otherCosts: number; repacking: number; freight: number; revenuesByType: Record<string, number> }> = {};

      settlements.forEach((s) => {
        const ex = s.exchange_rate || 1;
        const totalKilos = s.settlement_details.reduce((sum, d) => sum + (d.kilos || 0), 0);
        if (totalKilos === 0) return;

        s.settlement_details?.forEach((d) => {
          const variety = d.variety || t('Sin Variedad');
          if (!varietyData[variety]) {
            varietyData[variety] = { revenue: 0, commission: 0, tax: 0, otherCosts: 0, repacking: 0, freight: 0, revenuesByType: {} };
          }

          // Distribute settlement-level costs proportionally by kilos
          const kiloShare = d.kilos / totalKilos;
          varietyData[variety].revenue += ((s.total_revenue_local || 0) / ex) * kiloShare;
          varietyData[variety].commission += ((s.total_commission_local || 0) / ex) * kiloShare;
          varietyData[variety].tax += ((s.total_tax_local || 0) / ex) * kiloShare;
          varietyData[variety].otherCosts += ((s.total_other_costs_local || 0) / ex) * kiloShare;
          varietyData[variety].repacking += ((s.total_repacking_costs_local || 0) / ex) * kiloShare;
          varietyData[variety].freight += (s.total_freight_usd || 0) * kiloShare;

          // Detail revenues
          (d.settlement_detail_revenues || []).forEach((r: any) => {
            const type = r.revenue_type || 'other';
            const amountUsd = r.amount_usd || 0;
            revenueTypeSet.add(type);
            varietyData[variety].revenuesByType[type] = (varietyData[variety].revenuesByType[type] || 0) + amountUsd;
          });
        });
      });

      dataRows = Object.entries(varietyData).map(([label, data]) => ({
        label,
        revenue: data.revenue,
        commission: data.commission,
        tax: data.tax,
        otherCosts: data.otherCosts,
        repacking: data.repacking,
        freight: data.freight,
        fob: data.revenue - data.commission - data.tax - data.otherCosts - data.repacking - data.freight,
        revenuesByType: data.revenuesByType,
      }));
    } else {
      dataRows = Object.entries(groups).map(([label, subs]) => ({
        label,
        ...accumulate(subs),
      }));
    }

    // Total row
    const totalData = accumulate(settlements);
    const totalRow: FlowRow = {
      label: t('TOTAL'),
      isTotal: true,
      ...totalData,
    };

    const revenueTypes = Array.from(revenueTypeSet).sort();

    return {
      rows: [totalRow, ...dataRows],
      revenueTypes,
    };
  }, [settlements, activeTab, t]);

  const getTotalRevenues = (row: FlowRow) =>
    Object.values(row.revenuesByType).reduce((s, v) => s + v, 0);

  const getFobAjustado = (row: FlowRow) => row.fob + getTotalRevenues(row);

  const sortedRows = useMemo(() => {
    const [totalRow, ...dataRows] = rows;
    if (!totalRow) return [];

    const sorted = [...dataRows].sort((a, b) => {
      let cmp: number;
      if (sortKey === 'label') {
        cmp = a.label.localeCompare(b.label);
      } else if (sortKey === 'fobAjustado') {
        cmp = getFobAjustado(a) - getFobAjustado(b);
      } else if (['revenue', 'commission', 'tax', 'otherCosts', 'repacking', 'freight', 'fob'].includes(sortKey)) {
        cmp = (a[sortKey as keyof FlowRow] as number) - (b[sortKey as keyof FlowRow] as number);
      } else {
        // Revenue type column
        cmp = (a.revenuesByType[sortKey] || 0) - (b.revenuesByType[sortKey] || 0);
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });

    return [totalRow, ...sorted];
  }, [rows, sortKey, sortDir]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'label' ? 'asc' : 'desc');
    }
  };

  const SortIcon = ({ column }: { column: string }) => {
    if (sortKey === column) {
      return sortDir === 'asc' ? (
        <Icon icon='tabler:chevron-up' className='inline ml-0.5' width={10} />
      ) : (
        <Icon icon='tabler:chevron-down' className='inline ml-0.5' width={10} />
      );
    }
    return <Icon icon='tabler:chevron-down' className='inline ml-0.5 opacity-0 group-hover/th:opacity-40' width={10} />;
  };

  const hasRevenues = revenueTypes.length > 0;

  const handleExport = () => {
    const baseHeaders = [
      activeTab === 'client' ? t('Cliente') : activeTab === 'variety' ? t('Variedad') : t('País'),
      t('Ingreso'),
      t('Comisión'),
      t('Impuestos'),
      t('Flete'),
      t('Repacking'),
      t('Otros Costos'),
      t('FOB'),
    ];
    const revHeaders = revenueTypes.map((type) => t(REVENUE_TYPE_LABELS[type] || type).toUpperCase());
    const finalHeaders = hasRevenues
      ? [...baseHeaders, ...revHeaders, t('FOB Ajustado')]
      : baseHeaders;

    exportTableToExcel({
      fileName: `Flujos_por_${activeTab}`,
      sheetName: t('Flujos'),
      headers: finalHeaders,
      rows: sortedRows.map((row) => {
        const base = [
          row.label,
          Math.round(row.revenue),
          Math.round(row.commission),
          Math.round(row.tax),
          Math.round(row.freight),
          Math.round(row.repacking),
          Math.round(row.otherCosts),
          Math.round(row.fob),
        ];
        if (hasRevenues) {
          return [
            ...base,
            ...revenueTypes.map((type) => Math.round(row.revenuesByType[type] || 0)),
            Math.round(getFobAjustado(row)),
          ];
        }
        return base;
      }),
    });
  };

  useImperativeHandle(ref, () => ({ exportToExcel: handleExport }), [handleExport]);

  const fmtUsd = (v: number) => formatMoneyUSD(v, { decimals: 0 });

  // Get the total row values for percentage calculations
  const totalRow = sortedRows.find((r) => r.isTotal);

  const getPercent = (value: number, columnKey: string): number => {
    if (!totalRow) return 0;
    let total: number;
    if (columnKey === 'fobAjustado') {
      total = Math.abs(getFobAjustado(totalRow));
    } else if (['revenue', 'commission', 'tax', 'otherCosts', 'repacking', 'freight', 'fob'].includes(columnKey)) {
      total = Math.abs((totalRow as any)[columnKey] || 0);
    } else {
      total = Math.abs(totalRow.revenuesByType[columnKey] || 0);
    }
    if (total === 0) return 0;
    return (value / total) * 100;
  };

  const getBarWidth = (value: number, columnKey: string): number => {
    if (!totalRow) return 0;
    let total: number;
    if (columnKey === 'fobAjustado') {
      total = Math.abs(getFobAjustado(totalRow));
    } else if (['revenue', 'commission', 'tax', 'otherCosts', 'repacking', 'freight', 'fob'].includes(columnKey)) {
      total = Math.abs((totalRow as any)[columnKey] || 0);
    } else {
      total = Math.abs(totalRow.revenuesByType[columnKey] || 0);
    }
    if (total === 0) return 0;
    return (Math.abs(value) / total) * 100;
  };

  const FlowCell = ({ value, columnKey, bold = false, borderLeft = false }: { value: number; columnKey: string; bold?: boolean; borderLeft?: boolean }) => {
    const pct = getPercent(value, columnKey);
    const barW = getBarWidth(value, columnKey);
    const isEmpty = value === 0;
    const color = getColumnColor(columnKey);
    const colorClasses = COLOR_CLASSES[color];

    return (
      <td className={`py-1.5 px-3 text-right relative ${borderLeft ? 'border-l border-default-200' : ''}`}>
        {!isEmpty && (
          <div
            className={`absolute inset-y-0.5 right-0 rounded-sm ${colorClasses.bar}`}
            style={{ width: `${barW}%` }}
          />
        )}
        <div className='relative flex items-center justify-end gap-1.5'>
          {!isEmpty && (
            <span className='text-[10px] text-default-400 dark:text-default-500 tabular-nums'>
              {pct.toFixed(0)}%
            </span>
          )}
          <span className={bold ? 'font-semibold' : ''}>
            {isEmpty ? '—' : fmtUsd(value)}
          </span>
        </div>
      </td>
    );
  };

  const TotalCell = ({ value, columnKey, bold = false, borderLeft = false }: { value: number; columnKey: string; bold?: boolean; borderLeft?: boolean }) => {
    const color = getColumnColor(columnKey);
    const colorClasses = COLOR_CLASSES[color];
    return (
      <td className={`py-2 px-3 text-right ${borderLeft ? 'border-l border-default-200' : ''}`}>
        <span className={`${colorClasses.text} ${bold ? 'font-semibold' : ''}`}>
          {fmtUsd(value)}
        </span>
      </td>
    );
  };

  const firstColLabel = activeTab === 'client' ? t('CLIENTE') : activeTab === 'variety' ? t('VARIEDAD') : t('PAÍS');

  const fixedColumns: { key: SortKey; label: string }[] = [
    { key: 'revenue', label: t('INGRESO') },
    { key: 'commission', label: t('COMISIÓN') },
    { key: 'tax', label: t('IMPUESTOS') },
    { key: 'freight', label: t('FLETE') },
    { key: 'repacking', label: t('REPACKING') },
    { key: 'otherCosts', label: t('OTROS COSTOS') },
    { key: 'fob', label: 'FOB' },
  ];

  // Total colspan for the group header: fixed cols (7) + revenue types + fob ajustado if applicable
  const resultColSpan = 7;
  const otrosIngresosColSpan = hasRevenues ? revenueTypes.length + 1 : 0; // +1 for FOB Ajustado

  return (
    <div>
      <div className='overflow-x-auto'>
        <table className='w-full text-sm' style={{ minWidth: '1040px' }}>
          <thead>
            {hasRevenues && (
              <tr>
                <th style={{ width: '14%' }} />
                <th colSpan={resultColSpan} className='bg-default-100 text-center text-default-400 text-[11px] font-semibold py-1.5 border-b border-default-200'>
                  {t('COSTOS Y RESULTADOS')}
                </th>
                <th colSpan={otrosIngresosColSpan} className='bg-default-100 text-center text-default-400 text-[11px] font-semibold py-1.5 border-b border-default-200 border-l border-default-200'>
                  {t('OTROS INGRESOS')}
                </th>
              </tr>
            )}
            <tr>
              <th
                className={`${TH_BASE_CLASS} bg-default-100 text-left rounded-tl-md`}
                style={{ width: '14%' }}
                onClick={() => handleSort('label')}
              >
                {firstColLabel}
                <SortIcon column='label' />
              </th>
              {fixedColumns.map((col) => (
                <th
                  key={col.key}
                  className={`${TH_BASE_CLASS} bg-default-100 text-right`}
                  onClick={() => handleSort(col.key)}
                >
                  {col.label}
                  <SortIcon column={col.key} />
                </th>
              ))}
              {revenueTypes.map((type, i) => (
                <th
                  key={type}
                  className={`${TH_BASE_CLASS} bg-default-100 text-right ${i === 0 ? 'border-l border-default-200' : ''}`}
                  onClick={() => handleSort(type)}
                >
                  {t(REVENUE_TYPE_LABELS[type] || type).toUpperCase()}
                  <SortIcon column={type} />
                </th>
              ))}
              {hasRevenues && (
                <th
                  className={`${TH_BASE_CLASS} bg-default-100 text-right border-l border-default-200 rounded-tr-md`}
                  onClick={() => handleSort('fobAjustado')}
                >
                  FOB {t('AJUSTADO')}
                  <SortIcon column='fobAjustado' />
                </th>
              )}
              {!hasRevenues && (
                <th className='rounded-tr-md' style={{ width: 0 }} />
              )}
            </tr>
          </thead>
          <tbody>
            {(() => {
              // sortedRows[0] is always the totals row — always visible.
              // Data rows are paginated via visibleCount.
              const [totalRow, ...dataRows] = sortedRows;
              const visibleDataRows = dataRows.slice(0, visibleCount);
              const shown = totalRow
                ? [totalRow, ...visibleDataRows]
                : visibleDataRows;
              return shown;
            })().map((row) => (
              <tr
                key={row.label}
                className={row.isTotal
                  ? 'bg-default-100 font-semibold border-b-2 border-default-200'
                  : 'border-b border-default-100 hover:bg-default-50'}
              >
                <td className='py-2 px-3 font-medium'>{row.label}</td>
                {row.isTotal ? (
                  <>
                    <TotalCell value={row.revenue} columnKey='revenue' />
                    <TotalCell value={row.commission} columnKey='commission' />
                    <TotalCell value={row.tax} columnKey='tax' />
                    <TotalCell value={row.freight} columnKey='freight' />
                    <TotalCell value={row.repacking} columnKey='repacking' />
                    <TotalCell value={row.otherCosts} columnKey='otherCosts' />
                    <TotalCell value={row.fob} columnKey='fob' bold />
                    {revenueTypes.map((type, i) => (
                      <TotalCell key={type} value={row.revenuesByType[type] || 0} columnKey={type} borderLeft={i === 0} />
                    ))}
                    {hasRevenues && (
                      <TotalCell value={getFobAjustado(row)} columnKey='fobAjustado' bold borderLeft />
                    )}
                  </>
                ) : (
                  <>
                    <FlowCell value={row.revenue} columnKey='revenue' />
                    <FlowCell value={row.commission} columnKey='commission' />
                    <FlowCell value={row.tax} columnKey='tax' />
                    <FlowCell value={row.freight} columnKey='freight' />
                    <FlowCell value={row.repacking} columnKey='repacking' />
                    <FlowCell value={row.otherCosts} columnKey='otherCosts' />
                    <FlowCell value={row.fob} columnKey='fob' bold />
                    {revenueTypes.map((type, i) => (
                      <FlowCell key={type} value={row.revenuesByType[type] || 0} columnKey={type} borderLeft={i === 0} />
                    ))}
                    {hasRevenues && (
                      <FlowCell value={getFobAjustado(row)} columnKey='fobAjustado' bold borderLeft />
                    )}
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {(() => {
        const dataRowCount = Math.max(0, sortedRows.length - 1); // minus totals
        if (dataRowCount <= visibleCount) return null;
        return (
          <div className='flex justify-center mt-2'>
            <Button
              variant='light'
              size='sm'
              onPress={() =>
                setVisibleCount((c) => Math.min(c + 10, dataRowCount))
              }
              className='w-full'
            >
              {t('Cargar más')} ({dataRowCount - visibleCount}{' '}
              {t('restantes')})
            </Button>
          </div>
        );
      })()}
    </div>
  );
});

FlowsTable.displayName = 'FlowsTable';

export default React.memo(FlowsTable);
